// Files on HR's notes and comments: HR attaches them in Admin > Requests, the requester downloads
// the ones on comments to them. Bytes go straight to and from storage through short-lived links
// the API hands out (services/api/src/escalations.ts).

import { useRef, useState } from "react";
import {
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  attachmentSize,
  attachmentTypeFor,
  type Attachment,
} from "@ask-hr/core/escalations";
import { ApiError, api } from "./api.js";

export const ATTACHMENT_HINT = `PDF, Word, Excel, CSV, text, PNG or JPEG, up to 10 MB each, ${MAX_ATTACHMENTS} at a time.`;
export const ATTACHMENT_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg";

export const fileType = (f: File): string | undefined => attachmentTypeFor(f.name, f.type);

// Why a chosen file can't be attached, or undefined if it can.
export function fileProblem(f: File): string | undefined {
  if (!fileType(f)) return `${f.name} isn't a file type Ask HR can attach.`;
  if (f.size === 0) return `${f.name} is empty.`;
  if (f.size > MAX_ATTACHMENT_BYTES) return `${f.name} is over 10 MB.`;
  return undefined;
}

// Uploads each file and returns what to name in the note, comment or reply that adds them. base:
// the request's API path (HR's /api/admin/escalations/<id>, or the requester's /api/escalations/<id>).
export async function uploadAll(base: string, files: File[]): Promise<Pick<Attachment, "id" | "name" | "type">[]> {
  const out: Pick<Attachment, "id" | "name" | "type">[] = [];
  for (const f of files) {
    const type = fileType(f)!;
    const r = await api<{ attachment: Pick<Attachment, "id" | "name" | "type">; uploadUrl: string }>(
      "POST",
      `${base}/attachments`,
      { name: f.name, type, size: f.size },
    );
    const put = await fetch(r.uploadUrl, { method: "PUT", headers: { "content-type": type }, body: f });
    if (!put.ok) throw new Error(`${f.name} didn't upload. Try again.`);
    out.push(r.attachment);
  }
  return out;
}

// "Attach files" and the files chosen so far, each removable. Problems (wrong type, too big, too
// many) go to onProblem; the good files are kept.
export function AttachPicker({
  files,
  onChange,
  onProblem,
  disabled,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  onProblem: (problem: string | null) => void;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  function add(list: FileList | null) {
    const chosen = [...(list ?? [])];
    const bad = chosen.map(fileProblem).find(Boolean);
    onProblem(bad ?? (files.length + chosen.length > MAX_ATTACHMENTS ? `Attach up to ${MAX_ATTACHMENTS} files at a time.` : null));
    onChange([...files, ...chosen.filter((f) => !fileProblem(f))].slice(0, MAX_ATTACHMENTS));
    if (ref.current) ref.current.value = "";
  }
  return (
    <>
      <div className="attach-row">
        <label className="button">
          Attach files
          <input
            ref={ref}
            type="file"
            className="sr-only"
            multiple
            accept={ATTACHMENT_ACCEPT}
            disabled={disabled || files.length >= MAX_ATTACHMENTS}
            onChange={(e) => add(e.target.files)}
          />
        </label>
        <span className="muted small">{ATTACHMENT_HINT}</span>
      </div>
      {files.length ? (
        <ul className="attachments">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`}>
              {f.name}{" "}
              <button type="button" className="icon" aria-label={`Remove ${f.name}`} disabled={disabled} onClick={() => onChange(files.filter((_, j) => j !== i))}>
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

// Download links: each asks the API for a link, then the browser downloads it.
export function AttachmentLinks({ items, path }: { items: Attachment[]; path: (a: Attachment) => string }) {
  const [failed, setFailed] = useState<{ id: string; blocked: boolean } | null>(null);
  async function open(a: Attachment) {
    setFailed(null);
    try {
      const { url } = await api<{ url: string }>("GET", path(a));
      window.location.assign(url);
    } catch (e) {
      setFailed({ id: a.id, blocked: e instanceof ApiError && e.body.error === "attachment_blocked" });
    }
  }
  return (
    <ul className="attachments">
      {items.map((a) => (
        <li key={a.id}>
          <button type="button" className="link" onClick={() => void open(a)}>
            <PaperclipIcon />
            {a.name}
          </button>{" "}
          <span className="muted small">{attachmentSize(a.size)}</span>
          {failed?.id === a.id ? (
            <span className="small" role="alert">
              {" "}
              {failed.blocked ? "Blocked: the virus scan found a threat in this file. Tell IT if you need it." : "Couldn't download it. Try again."}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function PaperclipIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21 11.5 12.5 20a5 5 0 0 1-7-7L14 4.5a3.5 3.5 0 0 1 5 5L10.5 18a2 2 0 0 1-3-3l8-8" />
    </svg>
  );
}
